import { Check } from '@phosphor-icons/react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import wheelArt from '../../../../resources/images/birth-chart/birth-wheel.svg';
import ToastifyService, { TOAST_NOTIFY_TYPES } from '../../../../service/ToastifyService';
import {
    BIRTH_CHART_VIEW_SURFACE,
    closeBirthChartFlow,
    generateBirthChart,
    getBirthChartError,
    getBirthChartFlow,
    getBirthChartReport,
    getBirthChartStatus,
    openChartView,
    setFlowStep,
    toGenerateBody
} from '../../../../store/slices/birthChartSlice';
import { gaEvents } from '../../../../utils/analytics';
import { cityFromPlace, formatBirthDate } from './flowLogic';
import styles from './styles.module.scss';

export const MIN_LOADING_MS = 3000;
const STEP_COUNT = 4;

/**
 * "Traveling back in time" — owns the generate request. Runs for at least
 * MIN_LOADING_MS and until the request settles, ticking the checklist in
 * sequence across the minimum window.
 */
const LoadingStep = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const flow = useSelector(getBirthChartFlow);
    const status = useSelector(getBirthChartStatus);
    const report = useSelector(getBirthChartReport);
    const error = useSelector(getBirthChartError);

    const [requested, setRequested] = useState(false);
    const [minElapsed, setMinElapsed] = useState(false);
    const [active, setActive] = useState(0);
    const startedRef = useRef(false);
    const settledRef = useRef(false);

    // Start the request once (StrictMode double-runs effects; a request already
    // in flight from elsewhere is adopted rather than duplicated).
    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        if (status !== 'loading') {
            dispatch(generateBirthChart({ body: toGenerateBody(flow.details, flow.chatId), chatId: flow.chatId }));
        }
        setRequested(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Minimum duration + the checklist ticking.
    useEffect(() => {
        const start = Date.now();
        const interval = setInterval(() => {
            const elapsed = Date.now() - start;
            setActive(Math.min(STEP_COUNT, Math.floor((elapsed / MIN_LOADING_MS) * STEP_COUNT) + 1));
        }, 150);
        const timer = setTimeout(() => setMinElapsed(true), MIN_LOADING_MS);
        return () => {
            clearInterval(interval);
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        if (!requested || !minElapsed || settledRef.current) return;
        if (status === 'succeeded') {
            settledRef.current = true;
            const big = report?.content?.bigThree;
            gaEvents.birthChartLoadingSuccess(big?.sun?.sign, big?.moon?.sign, big?.rising?.sign);
            dispatch(closeBirthChartFlow());
            dispatch(openChartView({ surface: BIRTH_CHART_VIEW_SURFACE.POST_GENERATE }));
        } else if (status === 'failed') {
            settledRef.current = true;
            ToastifyService.setToast(
                typeof error === 'string' && error ? error : t('birthChart.flow.loading.failed'),
                TOAST_NOTIFY_TYPES.ERROR
            );
            dispatch(setFlowStep('verify'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requested, minElapsed, status]);

    const city = cityFromPlace(flow.details.place);
    const items = [
        city ? t('birthChart.flow.loading.locating', { city }) : t('birthChart.flow.loading.locatingFallback'),
        t('birthChart.flow.loading.reading', { date: formatBirthDate(flow.details.birthDate) }),
        t('birthChart.flow.loading.placing'),
        t('birthChart.flow.loading.rising')
    ];

    return (
        <div
            className={styles.loadingScreen}
            role="dialog"
            aria-modal="true"
            aria-busy="true"
            aria-label={t('birthChart.flow.loading.title')}
        >
            <h2 className={styles.loadingTitle}>{t('birthChart.flow.loading.title')}</h2>
            <img src={wheelArt} alt="" className={styles.loadingWheel} />
            <ul className={styles.checklist}>
                {items.map((label, i) => {
                    const done = i < active - 1 || (i === active - 1 && active === STEP_COUNT && minElapsed);
                    return (
                        <li key={label} className={classNames(styles.checkItem, { [styles.checkItemOn]: i < active })} aria-live="polite">
                            {done ? <Check size={14} weight="bold" aria-hidden="true" /> : null}
                            <span>{label}</span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default LoadingStep;
